/**
 * QuestionSetForm Component
 * Requirements: 7.2, 7.3, 7.4, 7.5, 7.6 - Create/Edit with 2-choice, 30-char limit, drag & drop
 * Requirement 49.7: Multi-language admin support with tabs and translation status
 */
import { useCallback, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Translate as TranslateIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { QuestionSet, CreateQuestionSetDTO } from '../types/questionSet.types';
import { createQuestionSetSchema, type CreateQuestionSetInput } from '../schemas/questionSet.schema';

/**
 * Supported languages for translation (Requirement 49.7)
 */
const TRANSLATION_LANGUAGES = [
  { code: 'en', label: 'English', field: 'textEn' as const },
  { code: 'id', label: 'Bahasa Indonesia', field: 'textId' as const },
  { code: 'mn', label: 'Монгол хэл', field: 'textMn' as const },
  { code: 'ne', label: 'नेपाली', field: 'textNe' as const },
  { code: 'vi', label: 'Tiếng Việt', field: 'textVi' as const },
] as const;

type TranslationField = (typeof TRANSLATION_LANGUAGES)[number]['field'];

interface QuestionSetFormProps {
  questionSet?: QuestionSet;
  onSubmit: (data: CreateQuestionSetDTO) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

interface SortableQuestionProps {
  id: string;
  index: number;
  control: ReturnType<typeof useForm<CreateQuestionSetInput>>['control'];
  register: ReturnType<typeof useForm<CreateQuestionSetInput>>['register'];
  errors: ReturnType<typeof useForm<CreateQuestionSetInput>>['formState']['errors'];
  onRemove: () => void;
  isSubmitting: boolean;
  watch: ReturnType<typeof useForm<CreateQuestionSetInput>>['watch'];
}

/**
 * Translation status indicator component (Requirement 49.7)
 */
function TranslationStatus({
  translatedCount,
  totalCount
}: {
  translatedCount: number;
  totalCount: number;
}) {
  const isComplete = translatedCount === totalCount;
  const hasPartial = translatedCount > 0 && translatedCount < totalCount;

  return (
    <Tooltip title={`${translatedCount}/${totalCount} 言語翻訳済み`}>
      <Chip
        size="small"
        icon={isComplete ? <CheckIcon /> : hasPartial ? <WarningIcon /> : undefined}
        label={`${translatedCount}/${totalCount}`}
        color={isComplete ? 'success' : hasPartial ? 'warning' : 'default'}
        variant="outlined"
        sx={{ ml: 1 }}
      />
    </Tooltip>
  );
}

/**
 * Multi-language input tabs for a question (Requirement 49.7)
 */
function QuestionTranslationTabs({
  index,
  register,
  watch,
  isSubmitting,
}: {
  index: number;
  register: ReturnType<typeof useForm<CreateQuestionSetInput>>['register'];
  watch: ReturnType<typeof useForm<CreateQuestionSetInput>>['watch'];
  isSubmitting: boolean;
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [expanded, setExpanded] = useState(false);

  // Count translated languages for question text
  const translatedCount = useMemo(() => {
    let count = 0;
    TRANSLATION_LANGUAGES.forEach(lang => {
      const value = watch(`questions.${index}.${lang.field}` as `questions.${number}.textEn`);
      if (value && value.trim() !== '') count++;
    });
    return count;
  }, [watch, index]);

  // Count translated languages for choice A
  const choiceATranslatedCount = useMemo(() => {
    let count = 0;
    TRANSLATION_LANGUAGES.forEach(lang => {
      const value = watch(`questions.${index}.answerChoices.0.${lang.field}` as `questions.${number}.answerChoices.0.textEn`);
      if (value && value.trim() !== '') count++;
    });
    return count;
  }, [watch, index]);

  // Count translated languages for choice B
  const choiceBTranslatedCount = useMemo(() => {
    let count = 0;
    TRANSLATION_LANGUAGES.forEach(lang => {
      const value = watch(`questions.${index}.answerChoices.1.${lang.field}` as `questions.${number}.answerChoices.1.textEn`);
      if (value && value.trim() !== '') count++;
    });
    return count;
  }, [watch, index]);

  const totalLanguages = TRANSLATION_LANGUAGES.length;

  return (
    <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1.5,
          bgcolor: 'grey.50',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'grey.100' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <TranslateIcon sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          多言語翻訳
        </Typography>
        <TranslationStatus translatedCount={translatedCount} totalCount={totalLanguages} />
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            {TRANSLATION_LANGUAGES.map((lang, langIndex) => {
              const hasValue = watch(`questions.${index}.${lang.field}` as `questions.${number}.textEn`);
              return (
                <Tab
                  key={lang.code}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {lang.label}
                      {hasValue && <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />}
                    </Box>
                  }
                />
              );
            })}
          </Tabs>

          {TRANSLATION_LANGUAGES.map((lang, langIndex) => (
            <Box
              key={lang.code}
              role="tabpanel"
              hidden={activeTab !== langIndex}
              sx={{ display: activeTab === langIndex ? 'block' : 'none' }}
            >
              {/* Question text translation */}
              <TextField
                {...register(`questions.${index}.${lang.field}` as `questions.${number}.textEn`)}
                label={`質問文 (${lang.label})`}
                fullWidth
                margin="normal"
                multiline
                rows={2}
                disabled={isSubmitting}
                placeholder={`${lang.label}の翻訳を入力（任意）`}
              />

              {/* Choice translations */}
              <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
                <Box flex={1} minWidth={200}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="body2" fontWeight="bold">
                        選択肢A ({lang.label})
                      </Typography>
                      <TranslationStatus
                        translatedCount={choiceATranslatedCount}
                        totalCount={totalLanguages}
                      />
                    </Box>
                    <TextField
                      {...register(`questions.${index}.answerChoices.0.${lang.field}` as `questions.${number}.answerChoices.0.textEn`)}
                      label={`選択肢A (${lang.label})`}
                      fullWidth
                      size="small"
                      disabled={isSubmitting}
                      placeholder={`${lang.label}の翻訳（任意）`}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Paper>
                </Box>

                <Box flex={1} minWidth={200}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="body2" fontWeight="bold">
                        選択肢B ({lang.label})
                      </Typography>
                      <TranslationStatus
                        translatedCount={choiceBTranslatedCount}
                        totalCount={totalLanguages}
                      />
                    </Box>
                    <TextField
                      {...register(`questions.${index}.answerChoices.1.${lang.field}` as `questions.${number}.answerChoices.1.textEn`)}
                      label={`選択肢B (${lang.label})`}
                      fullWidth
                      size="small"
                      disabled={isSubmitting}
                      placeholder={`${lang.label}の翻訳（任意）`}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Paper>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function SortableQuestion({
  id,
  index,
  control,
  register,
  errors,
  onRemove,
  isSubmitting,
  watch,
}: SortableQuestionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const choiceAText = watch(`questions.${index}.answerChoices.0.text`) || '';
  const choiceBText = watch(`questions.${index}.answerChoices.1.text`) || '';

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{ mb: 2 }}
      elevation={isDragging ? 4 : 1}
    >
      <CardContent>
        <Box display="flex" alignItems="flex-start" gap={1}>
          <IconButton
            {...attributes}
            {...listeners}
            aria-label="ドラッグして並び替え"
            sx={{ cursor: 'grab', mt: 1 }}
          >
            <DragIcon />
          </IconButton>

          <Box flex={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                質問 {index + 1}
              </Typography>
              <IconButton
                aria-label="質問を削除"
                onClick={onRemove}
                size="small"
                color="error"
                disabled={isSubmitting}
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            <TextField
              {...register(`questions.${index}.text`)}
              label={`質問文 ${index + 1} (日本語)`}
              fullWidth
              margin="normal"
              multiline
              rows={2}
              error={!!errors.questions?.[index]?.text}
              helperText={errors.questions?.[index]?.text?.message}
              disabled={isSubmitting}
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                選択肢 (2択) - 日本語
              </Typography>

              <Box display="flex" gap={2} flexWrap="wrap">
                {/* Choice A */}
                <Box flex={1} minWidth={250}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" fontWeight="bold" mb={1}>
                      選択肢A (日本語)
                    </Typography>
                    <TextField
                      {...register(`questions.${index}.answerChoices.0.text`)}
                      label="選択肢A テキスト"
                      fullWidth
                      size="small"
                      error={!!errors.questions?.[index]?.answerChoices?.[0]?.text}
                      helperText={
                        errors.questions?.[index]?.answerChoices?.[0]?.text?.message ||
                        `${choiceAText.length}/30`
                      }
                      disabled={isSubmitting}
                      inputProps={{ maxLength: 35 }}
                    />
                    <TextField
                      {...register(`questions.${index}.answerChoices.0.thinkingTypeCode`)}
                      label="選択肢A 思考傾向コード"
                      fullWidth
                      size="small"
                      sx={{ mt: 1 }}
                      error={!!errors.questions?.[index]?.answerChoices?.[0]?.thinkingTypeCode}
                      helperText={errors.questions?.[index]?.answerChoices?.[0]?.thinkingTypeCode?.message}
                      disabled={isSubmitting}
                    />
                    <input type="hidden" {...register(`questions.${index}.answerChoices.0.order`)} value={0} />
                  </Paper>
                </Box>

                {/* Choice B */}
                <Box flex={1} minWidth={250}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" fontWeight="bold" mb={1}>
                      選択肢B (日本語)
                    </Typography>
                    <TextField
                      {...register(`questions.${index}.answerChoices.1.text`)}
                      label="選択肢B テキスト"
                      fullWidth
                      size="small"
                      error={!!errors.questions?.[index]?.answerChoices?.[1]?.text}
                      helperText={
                        errors.questions?.[index]?.answerChoices?.[1]?.text?.message ||
                        `${choiceBText.length}/30`
                      }
                      disabled={isSubmitting}
                      inputProps={{ maxLength: 35 }}
                    />
                    <TextField
                      {...register(`questions.${index}.answerChoices.1.thinkingTypeCode`)}
                      label="選択肢B 思考傾向コード"
                      fullWidth
                      size="small"
                      sx={{ mt: 1 }}
                      error={!!errors.questions?.[index]?.answerChoices?.[1]?.thinkingTypeCode}
                      helperText={errors.questions?.[index]?.answerChoices?.[1]?.thinkingTypeCode?.message}
                      disabled={isSubmitting}
                    />
                    <input type="hidden" {...register(`questions.${index}.answerChoices.1.order`)} value={1} />
                  </Paper>
                </Box>
              </Box>
              {errors.questions?.[index]?.answerChoices?.message && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  {errors.questions?.[index]?.answerChoices?.message}
                </Typography>
              )}
            </Box>

            {/* Multi-language translation tabs (Requirement 49.7) */}
            <QuestionTranslationTabs
              index={index}
              register={register}
              watch={watch}
              isSubmitting={isSubmitting}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export function QuestionSetForm({
  questionSet,
  onSubmit,
  onCancel,
  isSubmitting,
}: QuestionSetFormProps) {
  const isEditMode = !!questionSet;

  const defaultValues: CreateQuestionSetInput = questionSet
    ? {
        title: questionSet.title,
        description: questionSet.description || '',
        questions: questionSet.questions.map((q, qIndex) => ({
          id: q.id,
          text: q.text,
          order: qIndex,
          // Multi-language fields (Requirement 49.7)
          textEn: q.textEn || '',
          textId: q.textId || '',
          textMn: q.textMn || '',
          textNe: q.textNe || '',
          textVi: q.textVi || '',
          answerChoices: q.answerChoices.map((c, cIndex) => ({
            id: c.id,
            text: c.text,
            thinkingTypeCode: c.thinkingTypeCode,
            order: cIndex,
            // Multi-language fields (Requirement 49.7)
            textEn: c.textEn || '',
            textId: c.textId || '',
            textMn: c.textMn || '',
            textNe: c.textNe || '',
            textVi: c.textVi || '',
          })),
        })),
      }
    : {
        title: '',
        description: '',
        questions: [],
      };

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreateQuestionSetInput>({
    resolver: zodResolver(createQuestionSetSchema),
    defaultValues,
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'questions',
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = fields.findIndex((f) => f.id === active.id);
        const newIndex = fields.findIndex((f) => f.id === over.id);
        move(oldIndex, newIndex);
      }
    },
    [fields, move]
  );

  const handleAddQuestion = useCallback(() => {
    append({
      text: '',
      order: fields.length,
      // Multi-language fields (Requirement 49.7)
      textEn: '',
      textId: '',
      textMn: '',
      textNe: '',
      textVi: '',
      answerChoices: [
        {
          text: '',
          thinkingTypeCode: '',
          order: 0,
          textEn: '',
          textId: '',
          textMn: '',
          textNe: '',
          textVi: '',
        },
        {
          text: '',
          thinkingTypeCode: '',
          order: 1,
          textEn: '',
          textId: '',
          textMn: '',
          textNe: '',
          textVi: '',
        },
      ],
    });
  }, [append, fields.length]);

  const handleFormSubmit = useCallback(
    (data: CreateQuestionSetInput) => {
      // Update order based on current position and include multi-language fields
      const submitData: CreateQuestionSetDTO = {
        ...data,
        questions: data.questions.map((q, index) => ({
          ...q,
          order: index,
          // Multi-language fields (Requirement 49.7) - only include non-empty values
          textEn: q.textEn?.trim() || undefined,
          textId: q.textId?.trim() || undefined,
          textMn: q.textMn?.trim() || undefined,
          textNe: q.textNe?.trim() || undefined,
          textVi: q.textVi?.trim() || undefined,
          answerChoices: q.answerChoices.map((c, cIndex) => ({
            ...c,
            order: cIndex,
            // Multi-language fields (Requirement 49.7) - only include non-empty values
            textEn: c.textEn?.trim() || undefined,
            textId: c.textId?.trim() || undefined,
            textMn: c.textMn?.trim() || undefined,
            textNe: c.textNe?.trim() || undefined,
            textVi: c.textVi?.trim() || undefined,
          })),
        })),
      };
      onSubmit(submitData);
    },
    [onSubmit]
  );

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h1">
          {isEditMode ? '質問セットを編集' : '質問セットを作成'}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <TextField
          {...register('title')}
          label="タイトル"
          fullWidth
          margin="normal"
          error={!!errors.title}
          helperText={errors.title?.message}
          disabled={isSubmitting}
          autoFocus
        />

        <TextField
          {...register('description')}
          label="説明"
          fullWidth
          margin="normal"
          multiline
          rows={3}
          error={!!errors.description}
          helperText={errors.description?.message}
          disabled={isSubmitting}
        />

        {isEditMode && (
          <Controller
            name="isPublished"
            control={control}
            defaultValue={questionSet?.isPublished || false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    {...field}
                    checked={field.value}
                    disabled={isSubmitting}
                  />
                }
                label="公開状態"
              />
            )}
          />
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">質問一覧</Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddQuestion}
          disabled={isSubmitting}
        >
          質問を追加
        </Button>
      </Box>

      {errors.questions?.message && (
        <Typography color="error" variant="body2" mb={2}>
          {errors.questions.message}
        </Typography>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={fields.map((f) => f.id)}
          strategy={verticalListSortingStrategy}
        >
          {fields.map((field, index) => (
            <SortableQuestion
              key={field.id}
              id={field.id}
              index={index}
              control={control}
              register={register}
              errors={errors}
              onRemove={() => remove(index)}
              isSubmitting={isSubmitting}
              watch={watch}
            />
          ))}
        </SortableContext>
      </DndContext>

      {fields.length === 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            color: 'text.secondary',
            borderStyle: 'dashed',
          }}
        >
          <Typography>
            質問がありません。「質問を追加」ボタンをクリックして質問を作成してください。
          </Typography>
        </Paper>
      )}

      <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
        <Button onClick={onCancel} disabled={isSubmitting}>
          キャンセル
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? '保存中...' : '保存'}
        </Button>
      </Box>
    </Box>
  );
}
